'use client';

import { Autocomplete, Stack, TextField, Typography } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { FormTextField } from '@/components/form/formTextField';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { Regions } from '@/data/inventory/regions';

import { CATALOG_NAME_RE, CatalogNameSection } from './CatalogNameSection';

import type { AddCatalogWizardState } from '../addCatalogWizardState';

interface RegionOption {
  value: string;
  label: string;
}

const GCP_REGION_OPTIONS: RegionOption[] = Object.entries(
  Regions[Provider.Gcp]
).map(([code, { displayName }]) => ({
  value: code,
  label: `${code} - ${displayName}`,
}));

export const CatalogBiglakeConfigStep = (
  props: StepProps<AddCatalogWizardState>
) => {
  const { catalogName, biglakeCatalogId, biglakeLocation } = props.wizardState;

  const canGoNext =
    CATALOG_NAME_RE.test(catalogName) &&
    biglakeCatalogId.trim() !== '' &&
    biglakeLocation.trim() !== '';

  const selectedRegion = useMemo(
    () => GCP_REGION_OPTIONS.find((o) => o.value === biglakeLocation) ?? null,
    [biglakeLocation]
  );

  return (
    <StepContainer
      stepperIndex={2}
      stepperLabels={props.stepperLabels}
      canGoNext={() => canGoNext}
      nextButtonText='Next'
      backButtonText='Back'
      onBackClick={props.back}
      onNextClick={async () => {
        props.setNextStep(props.currentStep.next?.[0]);
      }}
    >
      <Stack gap='32px' padding='24px 40px'>
        <CatalogNameSection
          value={catalogName}
          onChange={(value) =>
            props.setWizardState({
              ...props.wizardState,
              catalogName: value,
            })
          }
        />
        <Stack gap='16px'>
          <Typography variant='subtitle2' fontWeight={700}>
            BigLake Catalog Configuration
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Configure the BigLake Metastore catalog settings.
          </Typography>
        </Stack>

        <Stack gap='24px' maxWidth='540px'>
          <FormTextField
            title='Catalog ID'
            size='small'
            placeholder='e.g., my_biglake_catalog'
            value={biglakeCatalogId}
            onChange={(e) =>
              props.setWizardState({
                ...props.wizardState,
                biglakeCatalogId: e.target.value,
              })
            }
          />

          <Stack gap='8px'>
            <Typography variant='subtitle1'>Location (GCP Region)</Typography>
            <Autocomplete
              options={GCP_REGION_OPTIONS}
              value={selectedRegion}
              onChange={(_event, newValue) =>
                props.setWizardState({
                  ...props.wizardState,
                  biglakeLocation: newValue?.value ?? '',
                })
              }
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size='small'
                  placeholder='Search regions…'
                />
              )}
            />
          </Stack>
        </Stack>
      </Stack>
    </StepContainer>
  );
};
